import { useMemo } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui';
import {
  DEFAULT_VISIBLE_ORDER_COLUMNS,
  ORDER_COLUMN_LABELS,
  TOGGLEABLE_ORDER_COLUMNS,
  useOrderColumnPrefs,
} from '@/stores/useOrderColumnPrefs';

/** QA-536 주문 목록 [노출 항목] — 상품 목록 ProductColumnSettings 와 같은 드롭다운. */
export const OrderColumnSettings = () => {
  const visibleColumns = useOrderColumnPrefs((s) => s.visibleColumns);
  const toggleColumn = useOrderColumnPrefs((s) => s.toggleColumn);
  const resetColumns = useOrderColumnPrefs((s) => s.resetColumns);

  const visibleSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);
  const isDefault = useMemo(
    () =>
      visibleColumns.length === DEFAULT_VISIBLE_ORDER_COLUMNS.length &&
      DEFAULT_VISIBLE_ORDER_COLUMNS.every((c) => visibleSet.has(c)),
    [visibleColumns, visibleSet],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid="order-column-settings-button">
          <SlidersHorizontal size={14} aria-hidden="true" />
          노출 항목
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] min-w-[200px] overflow-y-auto">
        <DropdownMenuLabel>노출 항목 설정</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {TOGGLEABLE_ORDER_COLUMNS.map((column) => {
          const checked = visibleSet.has(column);
          return (
            <DropdownMenuCheckboxItem
              key={column}
              checked={checked}
              disabled={checked && visibleColumns.length <= 1}
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => toggleColumn(column)}
              data-testid={`order-column-toggle-${column}`}
            >
              {ORDER_COLUMN_LABELS[column]}
            </DropdownMenuCheckboxItem>
          );
        })}
        <DropdownMenuSeparator />
        <button
          type="button"
          onClick={resetColumns}
          disabled={isDefault}
          className="w-full rounded-sm px-2 py-1.5 text-left text-xs underline disabled:no-underline disabled:opacity-50"
          style={{ color: 'var(--content-muted)' }}
          data-testid="order-column-settings-reset"
        >
          기본값으로
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
